import React, { useEffect, useMemo, useState } from 'react';
import axios from "axios";
import { Typography, Layout, Select, Button, message } from 'antd';

import './SelfcareDashboardView.css';
import { apiUrl } from '../../../utility/Connections';
import LoadingOverlay from '../../../components/loadingOverlay/loadingOverlay';
import { getFromLocalStorage } from '../../../helpers/EncryptData';
import LeadershipSummary from '../../../components/informs/components/leadershipSummary/leadershipSummary';
import { getGlobalAverage } from '../../../utils/informUtils';

const { Title } = Typography;
const { Content } = Layout;
const { Option } = Select;

const DEFAULT_SRV_IDS = [6, 7];

const SelfcareDashboardView = ({ title = 'Dashboard' }) => {
    const [showLoading, setShowLoading] = useState(false);
    const [loadingText, setLoadingText] = useState("");

    const [dataEvaluations, setDataEvaluations] = useState([]);
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);

    const [totalResults, setTotalResults] = useState({});

    const headers = useMemo(() => {
        const token = localStorage.getItem('access_token');
        return {
            headers: {
                'Authorization': `Bearer ${token}`
            },
        };
    }, []);

    useEffect(() => {
        const current_org = getFromLocalStorage("current_org");
        if (current_org) {
            getEvaluations(current_org.org_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getEvaluationsBySrv = async (orgId, srvId) => {
        try {
            const response = await axios.get(`${apiUrl}/evaluations/get-full-evaluations/${orgId}/${srvId}/`, headers);
            return Array.isArray(response.data) ? response.data : [];
        } catch (error) {
            return [];
        }
    };

    const getEvaluations = async (orgId) => {
        setShowLoading(true);
        setLoadingText("Cargando información...");

        const results = await Promise.all(DEFAULT_SRV_IDS.map((srvId) => getEvaluationsBySrv(orgId, srvId)));
        const merged = results.flat();

        // Deduplicar por evl_id por seguridad
        const byId = new Map();
        for (const evl of merged) {
            if (evl?.evl_id != null) byId.set(evl.evl_id, evl);
        }

        const ordered = Array.from(byId.values()).sort((a, b) => (a.evl_id || 0) - (b.evl_id || 0));
        setDataEvaluations(ordered);

        if (ordered.length > 0) {
            const lastEvaluation = ordered[ordered.length - 1];
            setSelectedEvaluation(lastEvaluation);
            await getEvaluationResults(lastEvaluation.evl_id);
        }

        setShowLoading(false);
    };

    const getEvaluationResults = async (evl_id) => {
        setShowLoading(true);
        setLoadingText("Cargando información. Esto puede tardar un poco...");

        try {
            const response = await axios.get(`${apiUrl}/informs/get-evaluation-results/${evl_id}/`, headers);
            const data = response.data;
            setTotalResults(data.totals);
        } catch (error) {
            setTotalResults({});
        } finally {
            setShowLoading(false);
        }
    };

    const handleOnChangeEvaluation = async (evlId) => {
        const selected = dataEvaluations.find((evl) => evl.evl_id === evlId);
        if (!selected) return;
        setSelectedEvaluation(selected);
        await getEvaluationResults(selected.evl_id);
    };

    const seeGeneralInform = () => {
        if (!selectedEvaluation?.evl_id) return;
        const rootUrl = window.location.origin;
        const url = `${rootUrl}/informes/general/${selectedEvaluation.evl_id}`;
        window.open(url, '_blank');
    };

    const downloadEvaluationExcel = async () => {
        if (!selectedEvaluation?.evl_id) return;

        setShowLoading(true);
        setLoadingText("Generando archivo. Esto puede tardar un poco...");

        try {
            const token = localStorage.getItem('access_token');
            const excelHeaders = {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                responseType: 'blob'
            };

            const response = await axios.get(`${apiUrl}/informs/export-evaluation-results-excel/${selectedEvaluation.evl_id}/`, excelHeaders);

            const blob = new Blob([response.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Resultados ${selectedEvaluation.evl_title}.xlsx`);
            document.body.appendChild(link);
            link.click();

            link.remove();
            window.URL.revokeObjectURL(url);
            message.success("Archivo descargado");
        } catch (error) {
            message.error("Error exportando el archivo");
        } finally {
            setShowLoading(false);
        }
    };

    const dashboardTitle = `${title} - Seguridad y Autocuidado`;

    return (
        <div className='main-container'>
            {showLoading && <LoadingOverlay text={loadingText} showProgress={false} progress={null} />}

            {dataEvaluations.length === 0 && (
                <Content className='main-subcontainer main-subcontainer-column'>
                    <Title type='primary' level={4}>No se encontraron evaluaciones</Title>
                </Content>
            )}

            {dataEvaluations.length > 0 && (
                <Content className='main-subcontainer main-subcontainer-column'>
                    <div className='external-sub-header'>
                        <Title type='primary' level={2}>{dashboardTitle}</Title>
                        {/* <Select
                            style={{ width: 400 }}
                            value={selectedEvaluation?.evl_id}
                            onChange={handleOnChangeEvaluation}
                        >
                            {dataEvaluations.map((row) => (
                                <Option key={row.evl_id} value={row.evl_id}>
                                    {row.evl_title}
                                </Option>
                            ))}
                        </Select> */}
                    </div>

                    {/* {totalResults?.average_by_dimension && (
                        <div className='layout-content'>
                            <LeadershipSummary
                                data={totalResults}
                                globalAverage={getGlobalAverage(totalResults)}
                                title="Índice de seguridad y autocuidado"
                                redLight={selectedEvaluation.evl_max_red_light}
                                yellowLight={selectedEvaluation.evl_max_yellow_light}
                                isMain={true}
                            />

                            <div className='display-center-row align-full-center' style={{ marginTop: 20, marginBottom: 20 }}>
                                <Button type='primary' onClick={seeGeneralInform}>
                                    Ver informe general completo
                                </Button>
                                <Button style={{ marginLeft: 8 }} type='primary' onClick={downloadEvaluationExcel}>
                                    Ver informe general completo Excel
                                </Button>
                            </div>
                        </div>
                    )} */}
                </Content>
            )}
        </div>
    );
};

export default SelfcareDashboardView;
